import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Phone, MessageSquare, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { businessInfo } from '../data/mock';

const TracksPage = () => {
  const warningSigns = [
    "Door moves unevenly or jerks",
    "Visible gaps between rollers and track",
    "Bent or damaged track sections",
    "Door comes off track",
    "Grinding or scraping noises",
    "Door sticks during operation"
  ];

  const faqItems = [
    {
      q: "What causes garage door tracks to become misaligned?",
      a: "Impact from vehicles, loose hardware, worn rollers, or foundation settling can all cause track misalignment in Saskatoon homes."
    },
    {
      q: "Can bent tracks be repaired?",
      a: "Minor bends can often be straightened by professionals. Severely damaged tracks should be replaced for safe operation in Saskatoon."
    },
    {
      q: "How do I know if my tracks need adjustment?",
      a: "If your door moves unevenly, makes grinding noises, or has gaps between rollers and tracks, Saskatoon homeowners should call for service."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <section className="pt-32 pb-12 bg-gradient-to-r from-blue-900 to-blue-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Garage Door Tracks Saskatoon</h1>
            <p className="text-xl text-blue-100 mb-8">Professional track repair & alignment serving Saskatoon</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button onClick={() => window.location.href = `tel:${businessInfo.phone}`} size="lg" className="bg-green-600 hover:bg-green-700 text-white font-bold px-8 py-6 text-lg">
                <Phone size={20} className="mr-2" />Call {businessInfo.phone}
              </Button>
              <Button onClick={() => window.location.href = '/quote'} size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 font-bold px-8 py-6 text-lg">
                <MessageSquare size={20} className="mr-2" />Get Free Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-50 border-l-4 border-red-600 p-6 mb-8">
              <div className="flex items-start gap-3">
                <AlertTriangle size={32} className="text-red-600 flex-shrink-0" />
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Misaligned Tracks Cause Uneven Operation</h2>
                  <p className="text-gray-700 mb-3">
                    Garage door tracks guide your door's movement in Saskatoon homes. When tracks bend, shift, or become misaligned, your door moves unevenly and can come completely off track. <strong>306 Garage Doors</strong> provides professional track adjustment and repair for Saskatoon homeowners.
                  </p>
                  <p className="text-gray-700">
                    Properly aligned tracks ensure smooth, quiet operation and prevent costly damage to your door and opener in Saskatoon.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Signs Your Tracks Need Service</h2>
              <p className="text-xl text-gray-600">Watch for these issues in your Saskatoon garage</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {warningSigns.map((sign, idx) => (
                <Card key={idx} className="border-2 border-orange-200">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={20} className="text-orange-600 flex-shrink-0 mt-1" />
                      <p className="text-gray-800 font-semibold">{sign}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqItems.map((item, idx) => (
                <Card key={idx} className="border-2">
                  <CardHeader>
                    <CardTitle className="text-lg text-gray-900">{item.q}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700">{item.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-green-600 to-green-700">
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Need Track Service in Saskatoon?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">Professional track alignment and repair for smooth garage door operation in Saskatoon.</p>
          <Button onClick={() => window.location.href = `tel:${businessInfo.phone}`} size="lg" className="bg-white text-green-700 hover:bg-gray-100 font-bold px-12 py-8 text-xl">
            <Phone size={24} className="mr-2" />Call {businessInfo.phone}
          </Button>
          <p className="mt-6 text-green-100 italic">{businessInfo.confidentialMessage}</p>
        </div>
      </section>
    </div>
  );
};

export default TracksPage;